export type ChainType = 'evm' | 'other'

type ChainConfig = {
  chainId: number
  name: string
  symbol: string
  rpcUrls: string[]
  explorer: string
  type: ChainType
}

export const CHAIN_CONFIG = {
  ethereum: {
    chainId: 1,
    name: 'Ethereum',
    symbol: 'ETH',
    rpcUrls: [
      'https://cloudflare-eth.com',
      'https://ethereum.publicnode.com',
      'https://1rpc.io/eth',
    ],
    explorer: 'https://etherscan.io',
    type: 'evm',
  },
  base: {
    chainId: 8453,
    name: 'Base',
    symbol: 'ETH',
    rpcUrls: [
      'https://base-rpc.publicnode.com',
      'https://1rpc.io/base',
    ],
    explorer: 'https://basescan.org',
    type: 'evm',
  },
  arbitrum: {
    chainId: 42161,
    name: 'Arbitrum One',
    symbol: 'ETH',
    rpcUrls: [
      'https://arbitrum.publicnode.com',
      'https://1rpc.io/arb',
      'https://arb1.arbitrum.io/rpc',
    ],
    explorer: 'https://arbiscan.io',
    type: 'evm',
  },
  optimism: {
    chainId: 10,
    name: 'Optimism',
    symbol: 'ETH',
    rpcUrls: [
      'https://optimism.publicnode.com',
      'https://1rpc.io/op',
      'https://mainnet.optimism.io',
    ],
    explorer: 'https://optimistic.etherscan.io',
    type: 'evm',
  },
  polygon: {
    chainId: 137,
    name: 'Polygon',
    symbol: 'MATIC',
    rpcUrls: [
      'https://polygon.publicnode.com',
      'https://1rpc.io/matic',
      'https://polygon-rpc.com',
    ],
    explorer: 'https://polygonscan.com',
    type: 'evm',
  },
  bsc: {
    chainId: 56,
    name: 'BNB Chain',
    symbol: 'BNB',
    rpcUrls: [
      'https://bsc.publicnode.com',
      'https://1rpc.io/bnb',
      'https://bsc-dataseed.binance.org',
    ],
    explorer: 'https://bscscan.com',
    type: 'evm',
  },
  avalanche: {
    chainId: 43114,
    name: 'Avalanche C-Chain',
    symbol: 'AVAX',
    rpcUrls: [
      'https://avalanche.publicnode.com',
      'https://1rpc.io/avax',
      'https://api.avax.network/ext/bc/C/rpc',
    ],
    explorer: 'https://snowtrace.io',
    type: 'evm',
  },
} as const satisfies Record<string, ChainConfig>

export type EvmNetworkKey = keyof typeof CHAIN_CONFIG

export const SUPPORTED_CHAINS = Object.values(CHAIN_CONFIG)

export const SUPPORTED_EVM_CHAIN_KEYS: EvmNetworkKey[] = [
  'ethereum',
  'base',
  'arbitrum',
  'optimism',
  'polygon',
  'bsc',
  'avalanche',
]

export type EvmNetworkConfig = {
  id: number
  name: string
  rpcUrls: string[]
  rpcUrl: string
  explorer: string
  nativeSymbol: string
  nativeTokenId: string
  nativeName: string
  nativeCoingeckoId: string
  usdc?: {
    address: string
    decimals: number
    symbol: string
  }
}

export const EVM_NETWORKS: Record<EvmNetworkKey, EvmNetworkConfig> = {
  ethereum: {
    id: CHAIN_CONFIG.ethereum.chainId,
    name: CHAIN_CONFIG.ethereum.name,
    rpcUrls: CHAIN_CONFIG.ethereum.rpcUrls,
    rpcUrl: CHAIN_CONFIG.ethereum.rpcUrls[0],
    explorer: CHAIN_CONFIG.ethereum.explorer,
    nativeSymbol: 'ETH',
    nativeTokenId: 'eth',
    nativeName: 'Ether',
    nativeCoingeckoId: 'ethereum',
    usdc: {
      address: '0xA0b86991c6218b36c1d19D4a2e9Eb0cE3606eB48',
      decimals: 6,
      symbol: 'USDC',
    },
  },
  base: {
    id: CHAIN_CONFIG.base.chainId,
    name: CHAIN_CONFIG.base.name,
    rpcUrls: CHAIN_CONFIG.base.rpcUrls,
    rpcUrl: CHAIN_CONFIG.base.rpcUrls[0],
    explorer: CHAIN_CONFIG.base.explorer,
    nativeSymbol: 'ETH',
    nativeTokenId: 'eth',
    nativeName: 'Ether',
    nativeCoingeckoId: 'ethereum',
    usdc: {
      address: '0x833589fCD6eDb6E08f4c7C32D4f71b54bdA02913',
      decimals: 6,
      symbol: 'USDC',
    },
  },
  arbitrum: {
    id: CHAIN_CONFIG.arbitrum.chainId,
    name: CHAIN_CONFIG.arbitrum.name,
    rpcUrls: CHAIN_CONFIG.arbitrum.rpcUrls,
    rpcUrl: CHAIN_CONFIG.arbitrum.rpcUrls[0],
    explorer: CHAIN_CONFIG.arbitrum.explorer,
    nativeSymbol: 'ETH',
    nativeTokenId: 'eth',
    nativeName: 'Ether',
    nativeCoingeckoId: 'ethereum',
  },
  optimism: {
    id: CHAIN_CONFIG.optimism.chainId,
    name: CHAIN_CONFIG.optimism.name,
    rpcUrls: CHAIN_CONFIG.optimism.rpcUrls,
    rpcUrl: CHAIN_CONFIG.optimism.rpcUrls[0],
    explorer: CHAIN_CONFIG.optimism.explorer,
    nativeSymbol: 'ETH',
    nativeTokenId: 'eth',
    nativeName: 'Ether',
    nativeCoingeckoId: 'ethereum',
  },
  polygon: {
    id: CHAIN_CONFIG.polygon.chainId,
    name: CHAIN_CONFIG.polygon.name,
    rpcUrls: CHAIN_CONFIG.polygon.rpcUrls,
    rpcUrl: CHAIN_CONFIG.polygon.rpcUrls[0],
    explorer: CHAIN_CONFIG.polygon.explorer,
    nativeSymbol: 'MATIC',
    nativeTokenId: 'matic',
    nativeName: 'Polygon',
    nativeCoingeckoId: 'matic-network',
  },
  bsc: {
    id: CHAIN_CONFIG.bsc.chainId,
    name: CHAIN_CONFIG.bsc.name,
    rpcUrls: CHAIN_CONFIG.bsc.rpcUrls,
    rpcUrl: CHAIN_CONFIG.bsc.rpcUrls[0],
    explorer: CHAIN_CONFIG.bsc.explorer,
    nativeSymbol: 'BNB',
    nativeTokenId: 'bnb',
    nativeName: 'BNB',
    nativeCoingeckoId: 'binancecoin',
  },
  avalanche: {
    id: CHAIN_CONFIG.avalanche.chainId,
    name: CHAIN_CONFIG.avalanche.name,
    rpcUrls: CHAIN_CONFIG.avalanche.rpcUrls,
    rpcUrl: CHAIN_CONFIG.avalanche.rpcUrls[0],
    explorer: CHAIN_CONFIG.avalanche.explorer,
    nativeSymbol: 'AVAX',
    nativeTokenId: 'avax',
    nativeName: 'Avalanche',
    nativeCoingeckoId: 'avalanche-2',
  },
} as const

export const DEFAULT_EVM: EvmNetworkKey = 'base'

export type EvmTokenConfig = {
  id: string
  symbol: string
  name: string
  decimals: number
  address?: string
  coingeckoId: string
  showIfBalance?: boolean
}

export const EVM_TOKENS: Record<EvmNetworkKey, EvmTokenConfig[]> = {
  ethereum: [
    {
      id: 'eth',
      symbol: 'ETH',
      name: 'Ether',
      decimals: 18,
      coingeckoId: 'ethereum',
    },
    {
      id: 'usdc',
      symbol: 'USDC',
      name: 'USD Coin',
      decimals: 6,
      address: '0xA0b86991c6218b36c1d19D4a2e9Eb0cE3606eB48',
      coingeckoId: 'usd-coin',
    },
  ],
  base: [
    {
      id: 'eth',
      symbol: 'ETH',
      name: 'Ether',
      decimals: 18,
      coingeckoId: 'ethereum',
    },
    {
      id: 'usdc',
      symbol: 'USDC',
      name: 'USD Coin',
      decimals: 6,
      address: '0x833589fCD6eDb6E08f4c7C32D4f71b54bdA02913',
      coingeckoId: 'usd-coin',
    },
    {
      id: 'usdbc',
      symbol: 'USDbC',
      name: 'USD Base Coin',
      decimals: 6,
      address: '0xd9aaec86b65d86f6a7b5b1b0c42ffa531710b6ca',
      coingeckoId: 'bridged-usd-coin-base',
      showIfBalance: true,
    },
  ],
  arbitrum: [
    {
      id: 'eth',
      symbol: 'ETH',
      name: 'Ether',
      decimals: 18,
      coingeckoId: 'ethereum',
    },
  ],
  optimism: [
    {
      id: 'eth',
      symbol: 'ETH',
      name: 'Ether',
      decimals: 18,
      coingeckoId: 'ethereum',
    },
  ],
  polygon: [
    {
      id: 'matic',
      symbol: 'MATIC',
      name: 'Polygon',
      decimals: 18,
      coingeckoId: 'matic-network',
    },
  ],
  bsc: [
    {
      id: 'bnb',
      symbol: 'BNB',
      name: 'BNB',
      decimals: 18,
      coingeckoId: 'binancecoin',
    },
  ],
  avalanche: [
    {
      id: 'avax',
      symbol: 'AVAX',
      name: 'Avalanche',
      decimals: 18,
      coingeckoId: 'avalanche-2',
    },
  ],
}
